import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import { Trash2, Image as ImageIcon, Check, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
function resolveUrl(u) {
  if (!u) return null;
  if (u.startsWith('http')) return u;
  if (u.startsWith('/')) return `${API}${u}`;
  return u;
}

function FormuleCard({ formule, onSaved }) {
  const [platDuJour, setPlatDuJour] = useState(formule.platDuJour || '');
  const [ardoiseUrl, setArdoiseUrl] = useState(formule.ardoiseUrl || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // 'ok' | 'error'

  useEffect(() => {
    setPlatDuJour(formule.platDuJour || '');
    setArdoiseUrl(formule.ardoiseUrl || '');
  }, [formule.platDuJour, formule.ardoiseUrl]);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await adminApi.updateFormule(formule.id, { ...formule, platDuJour, ardoiseUrl });
      setStatus('ok');
      onSaved && onSaved({ ...formule, platDuJour, ardoiseUrl });
      setTimeout(() => setStatus(null), 2500);
    } catch {
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminApi.uploadFormuleImage(fd);
      setArdoiseUrl(res.data.url);
    } catch {
      setStatus('error');
    } finally {
      setUploading(false);
    }
  };

  const imgFullUrl = resolveUrl(ardoiseUrl);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-garden-200 p-5 space-y-4" data-testid={`platdujour-card-${formule.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-heading font-semibold text-garden-800">{formule.name}</h2>
        <span className="text-2xl font-heading font-bold text-garden-700 whitespace-nowrap">{formule.price} €</span>
      </div>
      {formule.description && <p className="text-xs text-charcoal/60 -mt-2">{formule.description}</p>}

      <div>
        <label className="block text-xs font-semibold text-garden-800 mb-1.5">Texte du jour (optionnel)</label>
        <textarea
          value={platDuJour}
          onChange={e => setPlatDuJour(e.target.value)}
          rows={5}
          placeholder="Ex : Magret de canard\nBurgers maison\nSalade César\nFish & Chips\nNems de canard\n(Un plat par ligne pour un affichage en liste)"
          className="w-full border border-garden-200 rounded-xl p-3 text-base focus:outline-none focus:ring-2 focus:ring-garden-300"
          data-testid={`text-${formule.id}`}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-garden-800 mb-1.5">Photo de l'ardoise (optionnelle)</label>
        {imgFullUrl ? (
          <div className="relative">
            <img src={imgFullUrl} alt="Ardoise" className="w-full max-h-56 object-contain rounded-xl border bg-cream/40" data-testid={`preview-${formule.id}`} />
            <button
              type="button"
              onClick={() => setArdoiseUrl('')}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow active:scale-95"
              aria-label="Supprimer la photo"
              data-testid={`remove-image-${formule.id}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-garden-300 rounded-xl py-8 cursor-pointer hover:bg-garden-50/50 active:bg-garden-100/40 transition-colors" data-testid={`upload-zone-${formule.id}`}>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid={`upload-input-${formule.id}`}
              onChange={e => handleUpload(e.target.files?.[0])}
            />
            {uploading ? (
              <Loader2 size={28} className="text-garden-500 animate-spin" />
            ) : (
              <>
                <ImageIcon size={28} className="text-garden-400" />
                <span className="text-sm text-garden-700 font-medium">Téléverser / Prendre une photo</span>
                <span className="text-[11px] text-charcoal/50">JPG / PNG · max 10 Mo</span>
              </>
            )}
          </label>
        )}
      </div>

      <p className="text-[11px] text-charcoal/50">Règle : si une photo est renseignée, elle est affichée en priorité côté site ; le texte sert alors de courte légende.</p>

      <div className="flex items-center justify-between gap-3 pt-1">
        {status === 'ok' && <span className="inline-flex items-center gap-1.5 text-sm text-green-700"><Check size={16} /> Enregistré</span>}
        {status === 'error' && <span className="inline-flex items-center gap-1.5 text-sm text-red-600"><AlertCircle size={16} /> Erreur</span>}
        {!status && <span />}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-5 py-3 rounded-full text-sm font-semibold text-white shadow active:scale-95 transition-all ${saving ? 'bg-charcoal/40' : 'bg-garden-800 hover:bg-garden-900'}`}
          data-testid={`save-${formule.id}`}
        >
          {saving ? 'Sauvegarde…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

export default function PlatDuJourAdmin() {
  const [formules, setFormules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.getFormules();
      setFormules((res.data || []).filter(f => f.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0) || (a.price || 0) - (b.price || 0)));
    } catch {
      setError('Impossible de charger les formules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateLocal = (next) => {
    setFormules(prev => prev.map(f => (f.id === next.id ? { ...f, ...next } : f)));
  };

  return (
    <AdminLayout title="Plat du Jour">
      <div className="max-w-2xl mx-auto" data-testid="platdujour-admin">
        <p className="text-sm text-charcoal/60 mb-6 -mt-2">
          Mettez à jour rapidement chaque formule. Pratique le matin depuis le téléphone : photo de l'ardoise ou texte tapé.
        </p>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-garden-600" size={28} /></div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
        ) : formules.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
            Aucune formule active. Créez d'abord vos formules dans <span className="font-semibold">Menu &gt; Formules</span>.
          </div>
        ) : (
          <div className="space-y-5">
            {formules.map(f => (
              <FormuleCard key={f.id} formule={f} onSaved={updateLocal} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
